import { ExceptionEvent } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { EXCEPTION_EVENT_WINDOW } from "@/constants/LookbackWindows";

/**
 * Hook to fetch ExceptionEvents grouped by ruleId.
 *
 * @param ruleIds - The IDs of the rules to filter ExceptionEvents.
 * @returns - A Record<string, ExceptionEvent[]> where the key is the ruleId.
 */
export function useExceptionEventsByRuleIds(ruleIds?: string[]) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { includeDismissedWarnings } = useDataGridSettings();
  ruleIds = ruleIds ? uniq(ruleIds) : ruleIds;

  return useQuery({
    queryKey: ["exceptionEvents", activeSessionId, ruleIds],

    queryFn: async ({ signal }) => {
      if (!ruleIds) {
        return Promise.resolve({} as Record<string, ExceptionEvent[]>);
      }

      const exceptionEvents = await Promise.all(
        ruleIds.map(ruleId =>
          geotabClient
            .get(
              "ExceptionEvent",
              {
                fromDate: new Date(new Date().getTime() - EXCEPTION_EVENT_WINDOW),
                includeDismissedEvents: includeDismissedWarnings ?? false,
                ruleSearch: { id: ruleId },
              },
              50000,
              signal,
            )
            .then(events => ({ ruleId, events })),
        ),
      );
      // Convert array of events into a record grouped by ruleId
      return exceptionEvents.reduce(
        (acc, { ruleId, events }) => {
          acc[ruleId] = events;
          return acc;
        },
        {} as Record<string, ExceptionEvent[]>,
      );
    },

    enabled: !!ruleIds,
  });
}
